import { useState } from "react";
import { getAvatarUrl, getInitials, getBrand, getMedalRing } from "@/lib/avatar";

/** Reusable user avatar with brand badge overlay and optional medal ring.
 * Props:
 *   user: { id, name, photo_path, avatar_style, avatar_seed, avatar_bg, avatar_brand }
 *   size: number (px)
 *   className: string (applied to the IMG/circle)
 *   testId: string
 *   topBadges: optional list of badge objects → applies medal ring
 *   showBrand: default true (set false to hide brand chip)
 */
export default function UserAvatar({ user, size = 48, className = "", testId, topBadges, showBrand = true }) {
  const [errored, setErrored] = useState(false);
  const url = !errored ? getAvatarUrl(user, size * 2) : null;
  const dim = `${size}px`;
  const bg = user?.avatar_bg ? `#${user.avatar_bg}` : "#1A1A1A";
  const brand = showBrand ? getBrand(user?.avatar_brand) : null;
  const ring = getMedalRing(topBadges);
  const ringStyle = ring
    ? { boxShadow: `0 0 0 3px ${ring.color}, 0 0 12px ${ring.glow}` }
    : null;

  // Brand badge sits at bottom-right of avatar. Size scales with avatar.
  const badgeSize = Math.max(16, Math.round(size * 0.32));

  const inner = url ? (
    <img
      src={url}
      alt={user?.name || "Avatar"}
      width={size}
      height={size}
      className={`object-cover ${className}`}
      style={{ width: dim, height: dim, ...(ringStyle || {}) }}
      onError={() => setErrored(true)}
      data-testid={testId || "user-avatar"}
    />
  ) : (
    <div
      className={`flex items-center justify-center font-bold text-black ${className}`}
      style={{ width: dim, height: dim, backgroundColor: bg, fontSize: size * 0.4, ...(ringStyle || {}) }}
      data-testid={testId || "user-avatar-fallback"}
    >
      {getInitials(user?.name)}
    </div>
  );

  if (!brand && !ring) return inner;

  return (
    <div className="relative inline-block" style={{ width: dim, height: dim }} data-testid={testId ? `${testId}-wrapper` : "user-avatar-wrapper"}>
      {inner}
      {brand && (
        <div
          title={`Patrocinador: ${brand.name}`}
          className="absolute -bottom-1 -right-1 flex items-center justify-center font-bold uppercase border-2 leading-none overflow-hidden"
          style={{
            width: badgeSize,
            height: badgeSize,
            backgroundColor: brand.primary,
            color: brand.accent,
            borderColor: "#0A0A0A",
            fontSize: Math.max(7, badgeSize * 0.35),
            letterSpacing: "0.5px",
          }}
          data-testid={`avatar-brand-${brand.id}`}
        >
          {brand.logo ? (
            <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain p-0.5" style={{ backgroundColor: brand.accent === "#FFFFFF" ? brand.primary : "#FFFFFF" }} />
          ) : (
            brand.short
          )}
        </div>
      )}
      {ring && size >= 56 && (
        <div
          className="absolute -top-1 -left-1 flex items-center justify-center font-bold uppercase leading-none border"
          style={{
            width: Math.max(18, Math.round(size * 0.28)),
            height: Math.max(18, Math.round(size * 0.28)),
            backgroundColor: ring.color,
            color: "#000",
            borderColor: "#0A0A0A",
            fontSize: Math.max(8, Math.round(size * 0.13)),
          }}
          title={`Tier: ${ring.label}`}
          data-testid="avatar-medal-tier"
        >
          ★
        </div>
      )}
    </div>
  );
}
